import dayjs from "dayjs";
import axios from "axios";
import { EMPLOYEE_DATA } from "../../Constants/Config";
import { ciUrl } from "../../Constants/nodeCi";
import {
  PO_LIST_PER_PAGE,
  STATUS_FILTER_API,
  DEFAULT_LIST_SORT,
} from "./constants";
import { formatMoneyForApi, formatStockDueForApi, clampPoLineQuantity, clampPoLineUnitCost, sanitizeOrderNumber } from "./createUtils";

/** Audit-log identity — mirrors Vendor_api_new/create_vendor. */
const resolveLoginEmployeeId = (auth, employeeId) => {
  const fromAuth = auth?.login_employee_id;
  if (fromAuth != null && String(fromAuth).trim() !== "") {
    return String(fromAuth);
  }
  return employeeId != null && String(employeeId).trim() !== ""
    ? String(employeeId)
    : "";
};

const withAuditLogAuth = (payload, auth, employeeId) => ({
  ...payload,
  login_type: auth?.login_type ?? "",
  login_employee_id: resolveLoginEmployeeId(auth, employeeId),
});

/** Map new_purchase_orders_vendor_list row → shared vendor option shape. */
export const mapNewPurchaseOrderVendor = (row) => ({
  id: String(row?.id ?? ""),
  name: row?.vendor_name || "",
  contact: row?.vendor_contact_name || "",
  terms: row?.payment_terms || "",
  products: Number(row?.total_assigned_products) || 0,
  preferred: String(row?.is_preferred_vendor || "").toLowerCase() === "yes",
});

/** Map API vendor list → VendorPicker / SupplierSelectDropdown options. */
export const mapNewPurchaseOrderVendorsList = (items, { sort = true } = {}) => {
  const mapped = (items || [])
    .map(mapNewPurchaseOrderVendor)
    .filter((v) => v.id);
  if (!sort) return mapped;
  return mapped.sort((a, b) => a.name.localeCompare(b.name));
};

/** Merge vendor pages / pinned rows — dedupe by id, then sort for display. */
export const mergeNewPurchaseOrderVendorOptions = (...groups) => {
  const byId = new Map();
  groups.flat().forEach((row) => {
    const vendor =
      row && typeof row === "object" && "id" in row && "name" in row
        ? row
        : mapNewPurchaseOrderVendor(row);
    if (vendor.id) byId.set(vendor.id, vendor);
  });
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/** Keep a selected vendor in the dropdown without a by-id vendor_list fetch. */
export const pinVendorInList = (vendors, pinned) => {
  if (!pinned) return vendors || [];
  const pinnedId = String(pinned?.id ?? "").trim();
  const pinnedName = String(pinned?.name ?? "").trim();
  if (!pinnedId && !pinnedName) return vendors || [];
  const list = vendors || [];
  const alreadyInList = list.some((v) => {
    if (pinnedId && String(v.id) === pinnedId) return true;
    return (
      pinnedName &&
      String(v.name || "")
        .trim()
        .toLowerCase() === pinnedName.toLowerCase()
    );
  });
  if (alreadyInList) return list;
  return [pinned, ...list];
};

/** Map category row → product-picker filter option. */
export const mapPurchaseOrderCategory = (row) => ({
  id: String(row?.id ?? ""),
  name: row?.title || row?.alternateName || row?.name || "Untitled",
});

/** Merge category pages — dedupe by id, sort by name. */
export const mergePurchaseOrderCategoryOptions = (...groups) => {
  const byId = new Map();
  groups.flat().forEach((row) => {
    const category =
      row && typeof row === "object" && "id" in row && "name" in row
        ? row
        : mapPurchaseOrderCategory(row);
    if (category.id) byId.set(category.id, category);
  });
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/** Keep a selected category visible when it is not on the loaded pages yet. */
export const pinCategoryInList = (categories, pinned) => {
  if (!pinned?.id) return categories || [];
  const list = categories || [];
  if (list.some((c) => String(c.id) === String(pinned.id))) return list;
  return mergePurchaseOrderCategoryOptions(list, [pinned]);
};

/** Map PO detail `vendor` → SupplierSelectDropdown option. */
export const vendorOptionFromPoVendor = (vendor, vendorId = "") => {
  const id = String(vendor?.id ?? vendorId ?? "").trim();
  const name = String(vendor?.name ?? "").trim();
  if (!id && !name) return null;
  return {
    id,
    name,
    contact: vendor?.contact || "",
    terms: vendor?.terms || "",
    products: Number(vendor?.products) || 0,
    preferred: Boolean(vendor?.preferred),
  };
};

/** Vendor id for ensureVendorId fetch — header id only, not cleared-empty assignments. */
export const resolvePurchaseOrderEnsureVendorId = (po = {}) => {
  const name = String(po?.vendor?.name || po?.vendorName || "").trim();
  const hasExplicitVendorId =
    po && Object.prototype.hasOwnProperty.call(po, "vendorId");
  const explicitId = hasExplicitVendorId
    ? normalizeVendorId(po.vendorId)
    : "";
  if (explicitId) return explicitId;
  if (hasExplicitVendorId && !name) return "";
  return normalizeVendorId(po?.vendor?.id);
};

/** getManagerStores row → Ordering for / Deliver to option. */
export const mapManagerStoreLocation = (store) => {
  const id = String(store?.merchant_id ?? "");
  const name = store?.name || "";
  const city = store?.city || "";
  const state = store?.state || "";
  const place = [city, state].filter(Boolean).join(", ");
  return {
    id,
    name,
    city,
    state,
    label: place ? `${name} · ${place}` : name,
  };
};

export const mapManagerStoreLocations = (stores) =>
  (stores || [])
    .map(mapManagerStoreLocation)
    .filter((l) => l.id)
    .sort((a, b) => a.name.localeCompare(b.name));

/** Manager-store row for a merchant_id (PDF ship/bill, location display). */
export const resolveStoreRecordByMerchantId = (stores, merchantId) => {
  const id = String(merchantId ?? "").trim();
  if (!id) return null;
  return (
    (stores || []).find((s) => String(s?.merchant_id ?? "") === id) || null
  );
};

/** Location label for a PO's ordering-for merchant id. */
export const resolvePoLocationDisplay = (stores, merchantId, fallbackName = "") => {
  const store = resolveStoreRecordByMerchantId(stores, merchantId);
  if (store) return mapManagerStoreLocation(store);
  const name = String(fallbackName || "").trim();
  if (!name) return null;
  return { id: String(merchantId ?? ""), name, city: "", state: "", label: name };
};

/** AllStore (localStorage) or LoginAllStore cookie — same source as Main.js fetch. */
/** generate_po_number — generate (no po_number) or duplicate check (with po_number). */
export const parseGeneratePoNumberResponse = (
  responseData,
  { checkingDuplicate = false } = {}
) => {
  if (!responseData || typeof responseData !== "object") {
    return {
      ok: false,
      poNumber: "",
      isDuplicate: false,
      message: "Invalid response",
      status: false,
    };
  }

  const status =
    responseData.status === true ||
    responseData.status === 1 ||
    responseData.status === "1" ||
    responseData.status === "true";

  const data = responseData.data;
  const dataObj =
    data && typeof data === "object" && !Array.isArray(data) ? data : null;

  const poNumber = String(
    dataObj?.po_number ??
    dataObj?.poNumber ??
    responseData.po_number ??
    (typeof data === "string" ? data : "") ??
    ""
  ).trim();

  const isDuplicate =
    dataObj?.is_duplicate === true ||
    dataObj?.is_duplicate === 1 ||
    dataObj?.is_duplicate === "1" ||
    dataObj?.duplicate === true ||
    dataObj?.duplicate === 1 ||
    (checkingDuplicate && !status);

  const message = String(
    responseData.message ?? dataObj?.message ?? ""
  ).trim();

  if (checkingDuplicate) {
    const duplicateByMessage = /already exists|duplicate|taken/i.test(message);
    const availableByMessage = /available/i.test(message);
    // API returns HTTP 200 for both outcomes; `status` in the body is authoritative.
    const available =
      status || (availableByMessage && !duplicateByMessage);
    const duplicate = !available;

    return {
      ok: available,
      poNumber,
      isDuplicate: duplicate,
      message,
      status: available,
    };
  }

  return {
    ok: status && Boolean(poNumber),
    poNumber,
    isDuplicate: false,
    message,
    status,
  };
};

/** Parsed generate_po_number availability check — true when number is free to use. */
export const isPoNumberAvailabilitySuccess = (parsed) => {
  if (!parsed || typeof parsed !== "object") return false;
  if (parsed.isDuplicate || parsed.ok === false) return false;
  if (parsed.ok) return true;
  return /available/i.test(String(parsed.message ?? "").trim());
};

/** AllStore localStorage — getManagerStores response written by Main.js on app load. */
export const resolveManagerStoresList = (storeData, loginAllStore) => {
  if (Array.isArray(storeData) && storeData.length > 0) return storeData;
  return [];
};

const INVALID_DATE_PREFIX = "0000-00-00";

export const normalizeApiDate = (value) => {
  if (!value || String(value).startsWith(INVALID_DATE_PREFIX)) return null;
  const s = String(value).trim();
  if (!s) return null;
  return s.includes("T") ? s.slice(0, 10) : s.slice(0, 10);
};

/** DB / route id — API field names vary across PO endpoints. */
export const resolvePurchaseOrderBackendId = (row) => {
  const raw =
    row?.id ??
    row?.purchase_order_id ??
    row?.po_id ??
    row?.purchase_id ??
    null;
  if (raw == null || raw === "") return null;
  return String(raw);
};

/**
 * `po_id` for update/activate/delete — prefer detail API id over route slug (po number).
 * update_new_purchase_order_items requires the existing draft's database id.
 */
export const resolvePurchaseOrderApiPoId = ({ po, details, routePoId } = {}) => {
  const header = po?._raw?.header || {};
  return (
    firstNonEmptyId(
      details?.backendId,
      details?.id,
      po?.backendId,
      resolvePurchaseOrderBackendId(header),
      resolvePurchaseOrderBackendId(po),
      po?.id
    ) || String(routePoId || "").trim()
  );
};

/** Map API row → UI purchase order shape. `index` ensures a stable unique `listKey`. */
export const mapApiPurchaseOrder = (row, index = 0) => {
  const status = mapApiStatus(row);
  const totalUnits =
    Number(row?.total_required_qty) ||
    Number(row?.total_units) ||
    Number(row?.total_qty) ||
    0;
  const totalReceived =
    Number(row?.total_received_qty) ||
    Number(row?.total_received) ||
    0;
  const totalCost = Number(row?.total_cost) || 0;
  const totalItems = Number(row?.total_items) || 0;
  const isPartial = status === "partial";
  const backendId = resolvePurchaseOrderBackendId(row);
  const poNumber =
    row?.po_number != null && row?.po_number !== ""
      ? String(row.po_number)
      : "";
  const vendorId = row?.vendor_id != null ? String(row.vendor_id) : "";
  const merchantId =
    row?.merchant_id != null && String(row.merchant_id).trim() !== ""
      ? String(row.merchant_id)
      : "";
  const listKey = `po-row-${index}-${backendId ?? poNumber ?? vendorId}`;

  return {
    listKey,
    id: poNumber || backendId || listKey,
    poNumber,
    backendId,
    merchantId,
    orderingFor:
      row?.order_for != null && String(row.order_for).trim() !== ""
        ? String(row.order_for)
        : merchantId,
    vendorId,
    vendorName: row?.vendor_name || "",
    reference: row?.reference || "",
    status,
    createdAt: normalizeApiDate(row?.created_at) || normalizeApiDate(row?.issued_date),
    expectedAt: normalizeApiDate(row?.stock_date),
    totalCost,
    avgCostPerUnit: Number(row?.avg_cost_per_unit) || 0,
    totalUnits,
    totalReceived,
    totalItems,
    lineItems: isPartial
      ? [
        {
          qtyOrdered: totalUnits,
          qtyReceived: totalReceived,
          unitCost: totalUnits ? totalCost / totalUnits : 0,
        },
      ]
      : Array.from({ length: Math.max(totalItems, 1) }, () => ({
        qtyOrdered: totalItems ? totalUnits / totalItems : totalUnits,
        qtyReceived: 0,
        unitCost: totalItems ? totalCost / totalItems : totalCost,
      })),
    _raw: row,
  };
};

export const mapApiStatus = (row) => {
  if (String(row?.is_void ?? row?.po_is_void) === "1") return "cancelled";
  if (String(row?.is_draft ?? row?.po_is_draft) === "1") return "draft";
  const received = String(row?.received_status ?? row?.po_received_status ?? "");
  if (received === "2") return "received";
  if (received === "1") return "partial";
  return "sent";
};

/** Listing vendor filter → API `search_by_vendor` (`""` = all vendors). */
export const resolveSearchByVendorParam = (vendorId) => {
  if (vendorId === "all" || vendorId == null || String(vendorId).trim() === "") {
    return "";
  }
  return String(vendorId);
};

export const buildListPayload = ({
  merchant_id,
  userTypeData,
  searchText,
  statusFilter,
  sortCol,
  sortOrd,
  vendorId,
  page = 1,
  perPage = PO_LIST_PER_PAGE,
}) => {
  const trimmedSearch = String(searchText || "").trim();
  const hasSearch = Boolean(trimmedSearch);
  const col = String(sortCol || DEFAULT_LIST_SORT.col).trim();
  const ord = String(sortOrd || DEFAULT_LIST_SORT.ord).toLowerCase() === "asc"
    ? "asc"
    : "desc";

  return {
    ...userTypeData,
    merchant_id,
    page: Number(page) || 1,
    per_page: perPage,
    search_by: hasSearch ? trimmedSearch : "",
    search_filters:
      statusFilter === "all" ? "" : STATUS_FILTER_API[statusFilter] ?? "",
    sort_col: col,
    sort_ord: ord,
    search_by_vendor: resolveSearchByVendorParam(vendorId),
  };
};

/** Parse numeric count fields (API returns strings). */
const countNum = (counts, key) => Number(counts?.[key]) || 0;

/**
 * new_purchase_orders_count → KPI summary cards only.
 * Keys: draft_po_count, due_po_count, open_po_count, total_cost, total_units.
 *
 * new_purchase_orders_list_count → quick-filter chip badges (via total_count).
 * Keys: all_po_count, active_po_count, draft_po_count, partial_po_count,
 * received_po_count, void_po_count. Scoped by search_by + search_by_vendor.
 */
export const mapCountToKpis = (counts) => {
  if (!counts) {
    return {
      open: 0,
      draft: 0,
      totalOpen: 0,
      incomingUnits: 0,
      overdueCount: 0,
    };
  }

  return {
    open: countNum(counts, "open_po_count"),
    draft: countNum(counts, "draft_po_count"),
    totalOpen: countNum(counts, "total_cost"),
    incomingUnits: countNum(counts, "total_units"),
    overdueCount: countNum(counts, "due_po_count"),
  };
};

/** new_purchase_orders_count → status quick-filter chip totals (merchant-wide). */
export const mapCountToStatusCounts = (counts) => {
  if (!counts) {
    return {
      all: 0,
      draft: 0,
      sent: 0,
      partial: 0,
      received: 0,
      cancelled: 0,
    };
  }

  return {
    all: countNum(counts, "all_po_count"),
    draft: countNum(counts, "draft_po_count"),
    sent: countNum(counts, "active_po_count"),
    partial: countNum(counts, "partial_po_count"),
    received: countNum(counts, "received_po_count"),
    cancelled: countNum(counts, "void_po_count"),
  };
};

/** UI status → API `received_status` (0=none, 1=partial, 2=received). */
export const statusToReceivedStatus = (status) => {
  if (status === "partial") return "1";
  if (status === "received") return "2";
  return "0";
};

/** Line `recieved_status` after receive (0=none, 1=partial, 2=full). */
export const lineItemReceivedStatus = (ordered, receivedTotal) => {
  const orderedN = Number(ordered) || 0;
  const receivedN = Number(receivedTotal) || 0;
  if (receivedN <= 0) return "0";
  if (receivedN < orderedN) return "1";
  return "2";
};

/** PO-level `received_status` from projected line items. */
export const computePoReceivedStatusFromLines = (lineItems) => {
  const lines = lineItems || [];
  if (!lines.length) return "0";

  let anyReceived = false;
  let allComplete = true;

  for (const li of lines) {
    const ordered = Number(li.qtyOrdered) || 0;
    const received = Number(li.qtyReceived) || 0;
    if (received > 0) anyReceived = true;
    if (ordered > 0 && received < ordered) allComplete = false;
  }

  if (!anyReceived) return "0";
  if (allComplete) return "2";
  return "1";
};

/**
 * Map SOH-picked lines → `order_items` for update_new_purchase_order_items_soh.
 * Do not send `po_item_id` — the SOH endpoint matches by product identity.
 */
export const buildSohUpdateOrderItemsPayload = (lineItems) => {
  const eligible = (lineItems || []).filter(
    (li) => Number(li?.qtyOrdered) > 0 && String(li?.productId || "").trim()
  );
  return eligible.map((li) => {
    const required = Number(li.qtyOrdered) || 0;
    const received = Number(li.qtyReceived) || 0;
    const pending = Math.max(0, required - received);
    const cost = Number(li.unitCost) || 0;
    const price = Number(li.price) || 0;
    const inventory = Number(li.currentInventory) || 0;
    const variantId =
      li.variantId != null && String(li.variantId).trim() !== ""
        ? String(li.variantId)
        : "";

    return {
      product_id: String(li.productId || ""),
      variant_id: variantId,
      upc: String(li.upc || li.sku || ""),
      required_qty: required,
      recieved_qty: received,
      recieved_status: lineItemReceivedStatus(required, received),
      pending_qty: pending,
      cost_per_item: Number(formatMoneyForApi(cost)),
      price: Number(formatMoneyForApi(price)),
      total_pricing: Number(formatMoneyForApi(required * cost)),
      after_qty: inventory + required,
      note: String(li.note || ""),
    };
  });
};

/** Map UI line items → `order_items` for update_new_purchase_order_items. */
export const buildUpdateOrderItemsPayload = (lineItems) => {
  const eligible = (lineItems || []).filter(
    (li) => Number(li?.qtyOrdered) > 0
  );
  return eligible.reduce((acc, li, index) => {
    const required = Number(li.qtyOrdered) || 0;
    const received = Number(li.qtyReceived) || 0;
    const pending = Math.max(0, required - received);
    const cost = Number(li.unitCost) || 0;
    // Snapshot selling price from product API — do not recompute or edit.
    const price = Number(li.price) || 0;
    const inventory = Number(li.currentInventory) || 0;
    const variantId =
      li.variantId != null && String(li.variantId).trim() !== ""
        ? String(li.variantId)
        : "";
    const poItemId = String(li.poItemId ?? li.po_item_id ?? "").trim();

    acc[index] = {
      po_item_id: poItemId,
      product_id: String(li.productId || ""),
      variant_id: variantId,
      required_qty: String(required),
      recieved_qty: String(received),
      pending_qty: String(pending),
      recieved_status: lineItemReceivedStatus(required, received),
      cost_per_item: formatMoneyForApi(cost),
      price: formatMoneyForApi(price),
      total_pricing: formatMoneyForApi(required * cost),
      upc: String(li.sku || li.upc || ""),
      note: String(li.note || ""),
      after_qty: String(inventory + required),
    };
    return acc;
  }, {});
};

/** Map UI line items → `order_items` object for add_new_purchase_order. */
export const buildOrderItemsPayload = (lineItems) => {
  const eligible = (lineItems || []).filter(
    (li) => Number(li?.qtyOrdered) > 0
  );
  return eligible.reduce((acc, li, index) => {
    const qty = Number(li.qtyOrdered) || 0;
    const cost = Number(li.unitCost) || 0;
    // Snapshot selling price from product API — do not recompute or edit.
    const price = Number(li.price) || 0;
    const inventory = Number(li.currentInventory) || 0;
    const variantId =
      li.variantId != null && String(li.variantId).trim() !== ""
        ? String(li.variantId)
        : "";

    acc[index] = {
      product_id: String(li.productId || ""),
      variant_id: variantId,
      required_qty: String(qty),
      recieved_qty: "0",
      pending_qty: String(qty),
      after_qty: String(inventory + qty),
      cost_per_item: formatMoneyForApi(cost),
      price: formatMoneyForApi(price),
      total_pricing: formatMoneyForApi(qty * cost),
      upc: String(li.sku || li.upc || ""),
      note: "",
      title: String(li.title || li.label || ""),
      external_id: "",
    };
    return acc;
  }, {});
};

/** Build multipart body for new_purchase_orders_api/add_new_purchase_order. */
export const buildAddPurchaseOrderPayload = ({
  auth,
  merchantId,
  employeeId,
  poNumber,
  vendorId,
  vendorName,
  orderingFor,
  deliverTo,
  supplierInvoice,
  stockDueDate,
  note,
  discountMode,
  discountValue,
  shipping,
  importDuty,
  lineItems,
  isDraft = true,
  stores = "",
}) => {
  const orderItems = buildOrderItemsPayload(lineItems);
  const isPercent = discountMode === "pct" ? "1" : "0";

  // Always send vendor_* / stores as strings so multipart keeps the keys.
  // Vendor is optional — empty string means no vendor (null would be dropped).
  return withAuditLogAuth(
    {
      ...auth,
      merchant_id: merchantId,
      admin_id: merchantId,
      employee_id: employeeId ? String(employeeId) : "",
      po_number: sanitizeOrderNumber(poNumber).trim(),
      vendor_id: resolveVendorIdForApi(vendorId) ?? "",
      vendor_name: resolveVendorNameForApi(vendorId, vendorName) ?? "",
      vendor_email: "",
      issued_date: dayjs().format("YYYY-MM-DD"),
      stock_date: formatStockDueForApi(stockDueDate),
      reference: String(note || ""),
      inv_number: String(supplierInvoice || ""),
      is_draft: isDraft ? "1" : "0",
      is_void: "0",
      received_status: "0",
      received_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      order_for: String(orderingFor || ""),
      deliver_to: String(deliverTo || ""),
      is_percent: isPercent,
      po_discount: formatMoneyForApi(discountValue),
      total_shipping: formatMoneyForApi(shipping),
      total_import_duty: formatMoneyForApi(importDuty),
      order_items: JSON.stringify(orderItems),
      stores: String(stores || ""),
    },
    auth,
    employeeId
  );
};

/** Build multipart body for new_purchase_orders_api/add_new_purchase_orders_from_csv. */
export const buildAddPurchaseOrderFromCsvPayload = ({
  auth,
  merchantId,
  employeeId,
  poNumber,
  vendorId,
  vendorName,
  vendorEmail,
  orderingFor,
  deliverTo,
  supplierInvoice,
  stockDueDate,
  note,
  discountMode,
  discountValue,
  shipping,
  importDuty,
  csvFile,
  isDraft = true,
  stores = "",
}) => {
  const isPercent = discountMode === "pct" ? "1" : "0";

  return withAuditLogAuth(
    {
      ...auth,
      merchant_id: merchantId,
      admin_id: merchantId,
      employee_id: employeeId ? String(employeeId) : "",
      po_number: sanitizeOrderNumber(poNumber).trim(),
      vendor_id: resolveVendorIdForApi(vendorId),
      vendor_name: resolveVendorNameForApi(vendorId, vendorName),
      vendor_email: String(vendorEmail || ""),
      issued_date: dayjs().format("YYYY-MM-DD"),
      stock_date: formatStockDueForApi(stockDueDate),
      reference: String(note || ""),
      note: String(note || ""),
      inv_number: String(supplierInvoice || ""),
      is_draft: isDraft ? "1" : "0",
      is_void: "0",
      received_status: "0",
      received_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      order_for: String(orderingFor || ""),
      deliver_to: String(deliverTo || ""),
      is_percent: isPercent,
      po_discount: formatMoneyForApi(discountValue),
      total_shipping: formatMoneyForApi(shipping),
      total_import_duty: formatMoneyForApi(importDuty),
      csv_file: csvFile,
      stores: String(stores || ""),
    },
    auth,
    employeeId
  );
};

const coerceOrderItemsArray = (raw) => {
  if (!raw) return [];
  if (typeof raw === "string") {
    try {
      return coerceOrderItemsArray(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    return Object.values(raw).filter(
      (entry) => entry && typeof entry === "object" && !Array.isArray(entry)
    );
  }
  return [];
};

const normalizePoItemVariantId = (value) => {
  if (value == null) return "";
  const normalized = String(value).trim();
  return normalized && normalized !== "0" ? normalized : "";
};

const mapApiOrderItem = (item, index) => {
  const qtyOrdered =
    Number(
      item?.ordered_qty ??
        item?.required_qty ??
        item?.pending_qty ??
        item?.qty
    ) || 0;
  const qtyReceived =
    Number(
      item?.received_qty ?? item?.recieved_qty ?? item?.item_qty ?? 0
    ) || 0;
  const productTitle = item?.product_title || item?.title || "";
  const variantName = item?.variant_name || item?.variant_title || "";
  const label =
    item?.product_name ||
    (productTitle && variantName
      ? `${productTitle} · ${String(variantName).replace(/\//g, " / ")}`
      : productTitle || variantName || item?.title || "Line item");
  const productId = String(
    item?.product_id ??
      item?.productId ??
      item?.prod_id ??
      item?.parent_product_id ??
      ""
  ).trim();
  const variantId = normalizePoItemVariantId(
    item?.variant_id ?? item?.varient_id ?? item?.variantId
  );
  const upc = String(item?.upc ?? item?.sku ?? item?.barcode ?? "").trim();
  const poItemId = String(
    item?.po_item_id ?? item?.order_item_id ?? ""
  ).trim();

  return {
    variantKey: poItemId || `${productId || "item"}-${variantId || upc || index}`,
    poItemId: poItemId || undefined,
    label,
    sku: upc,
    upc,
    qtyOrdered: clampPoLineQuantity(qtyOrdered, qtyReceived),
    qtyReceived: clampPoLineQuantity(qtyReceived, 0),
    unitCost: clampPoLineUnitCost(Number(item?.unit_cost ?? item?.cost_per_item) || 0),
    price: Number(item?.price) || 0,
    subtotal:
      item?.subtotal != null && item?.subtotal !== ""
        ? Number(item.subtotal) || 0
        : undefined,
    note: item?.note || "",
    productId: productId || undefined,
    variantId: variantId || undefined,
    currentInventory:
      item?.after_qty != null && item?.after_qty !== ""
        ? Math.max(
            0,
            Number(item.after_qty) -
              (Number(
                item?.ordered_qty ?? item?.required_qty ?? item?.pending_qty
              ) || 0)
          )
        : Number(item?.current_inventory ?? item?.quantity) || 0,
  };
};

const resolvePoDetailHeader = (raw) => {
  if (raw?.po_data && typeof raw.po_data === "object") return raw.po_data;
  if (raw?.purchase_order && typeof raw.purchase_order === "object") {
    return raw.purchase_order;
  }
  return raw && typeof raw === "object" ? raw : {};
};

const resolvePoDetailItems = (raw, header) =>
  raw?.po_items_data ?? header?.order_items ?? raw?.order_items;

const firstNonEmptyId = (...values) => {
  for (const value of values) {
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
};

const normalizeVendorId = (value) => {
  const id = String(value ?? "").trim();
  return id && id !== "0" ? id : "";
};

/** Multipart API — null when vendor is unassigned, string when set. */
const resolveVendorIdForApi = (vendorId) => {
  const id = normalizeVendorId(vendorId);
  return id || null;
};

const resolveVendorNameForApi = (vendorId, vendorName) => {
  const id = normalizeVendorId(vendorId);
  const name = String(vendorName ?? "").trim();
  // Never send a name without an id — the update API treats that as clearing the vendor.
  if (!id) return null;
  return name || null;
};

/** Resolve vendor_id from a PO view model or API header (listing, detail, edit). */
export const resolvePurchaseOrderVendorId = (source = {}, firstItem) => {
  const header = source?._raw?.header;
  const fromNested = () =>
    normalizeVendorId(
      firstNonEmptyId(
        source?.vendor?.id,
        source?.vendor_id,
        source?.po_vendor_id,
        header?.vendor_id,
        header?.po_vendor_id,
        header?.vendorId,
        firstItem?.vendor_id,
        firstItem?.po_vendor_id
      )
    );

  if (source && Object.prototype.hasOwnProperty.call(source, "vendorId")) {
    const fromField = normalizeVendorId(source.vendorId);
    if (fromField) return fromField;
    // Empty vendorId with no name = intentionally cleared ("No Vendor").
    const name = String(source?.vendor?.name || source?.vendorName || "").trim();
    if (!name) return "";
    // Name still present — recover id from vendor object / header fallbacks.
    return fromNested();
  }

  return fromNested();
};

/** Fallback when detail API omits vendor_id — match vendor name against the vendor list. */
export const resolvePurchaseOrderVendorIdFromVendors = (po, vendors = []) => {
  const name = String(po?.vendor?.name || po?.vendorName || "").trim();
  const hasExplicitVendorId =
    po && Object.prototype.hasOwnProperty.call(po, "vendorId");
  const explicitId = hasExplicitVendorId
    ? normalizeVendorId(po.vendorId)
    : null;

  if (explicitId) return explicitId;
  if (hasExplicitVendorId && !name) return "";

  const direct = resolvePurchaseOrderVendorId(po);
  if (direct) return direct;

  if (!name) return "";
  const match = (vendors || []).find(
    (vendor) =>
      String(vendor?.name || "")
        .trim()
        .toLowerCase() === name.toLowerCase()
  );
  return match?.id ? String(match.id) : "";
};

/** Merge detail API payload without dropping vendor_id from list navigation / cache. */
export const mergePurchaseOrderDetail = (
  prev,
  detail,
  vendors = [],
  { preserveLineItems = false, preserveVendor = false } = {}
) => {
  if (!detail) return prev;
  const detailHasExplicitVendorId =
    detail && Object.prototype.hasOwnProperty.call(detail, "vendorId");
  const vendorId = detailHasExplicitVendorId
    ? normalizeVendorId(detail.vendorId)
    : resolvePurchaseOrderVendorId(detail) ||
      resolvePurchaseOrderVendorId(prev) ||
      resolvePurchaseOrderVendorIdFromVendors(detail, vendors) ||
      resolvePurchaseOrderVendorIdFromVendors(prev, vendors) ||
      "";

  let vendor;
  if (detailHasExplicitVendorId) {
    vendor =
      detail.vendor ??
      (vendorId
        ? {
            id: vendorId,
            name:
              detail.vendor?.name ||
              detail.vendorName ||
              prev.vendor?.name ||
              "",
          }
        : String(detail.vendorName || detail.vendor?.name || "").trim()
          ? {
              id: "",
              name: String(detail.vendorName || detail.vendor?.name || "").trim(),
              email: detail.vendor?.email || "",
              phone: detail.vendor?.phone || "",
              contact: detail.vendor?.contact || "",
              terms: detail.vendor?.terms || "",
            }
          : null);
  } else {
    const vendorBase = detail.vendor || prev.vendor;
    vendor = vendorBase
      ? { ...vendorBase, id: vendorId || vendorBase.id || "" }
      : vendorId
        ? {
            id: vendorId,
            name: detail.vendor?.name || prev.vendor?.name || "",
          }
        : null;
  }

  const lineItems = preserveLineItems
    ? prev.lineItems || []
    : Array.isArray(detail.lineItems)
      ? detail.lineItems
      : [];

  return {
    ...prev,
    ...detail,
    lineItems,
    totalItems:
      detail.noProducts || Array.isArray(detail.lineItems)
        ? (detail.lineItems || []).length
        : prev.totalItems,
    totalUnits: detail.noProducts
      ? 0
      : detail.totalUnits != null
        ? detail.totalUnits
        : prev.totalUnits,
    totalReceived: detail.noProducts
      ? 0
      : detail.totalReceived != null
        ? detail.totalReceived
        : prev.totalReceived,
    totalCost: detail.noProducts
      ? 0
      : detail.totalCost != null
        ? detail.totalCost
        : prev.totalCost,
    vendorId: preserveVendor ? prev.vendorId : vendorId,
    vendor: preserveVendor ? prev.vendor : vendor,
  };
};

const coerceReceiveHistoryArray = (raw) => {
  const list = raw?.po_receive_history ?? raw?.receive_history ?? [];
  return Array.isArray(list) ? list : [];
};

/** Map one `po_receive_history` row → receive-history card shape. */
export const mapApiReceiveHistoryItem = (row) => ({
  id: String(row?.id ?? ""),
  unitsReceived: Number(row?.total_units) || 0,
  receivedAt:
    normalizeApiDate(row?.created_at) ||
    normalizeApiDate(row?.updated_at) ||
    null,
  receivedBy: String(row?.employee_name || "").trim(),
  note:
    row?.note != null && String(row.note).trim() !== ""
      ? String(row.note)
      : "",
  affectedLineCount: Number(row?.affected_lines) || 0,
});

/** Map `po_receive_history` from PO details — newest receipt first. */
export const mapApiReceiveHistory = (raw) =>
  coerceReceiveHistoryArray(raw)
    .slice()
    .sort((a, b) =>
      String(b?.created_at || b?.updated_at || "").localeCompare(
        String(a?.created_at || a?.updated_at || "")
      )
    )
    .map(mapApiReceiveHistoryItem);

/** Map PO detail header + line items → vendor id/name for display (read path). */
const resolveApiPoVendorFields = (header = {}, firstItem = {}) => {
  const headerVendorId = normalizeVendorId(
    firstNonEmptyId(header?.vendor_id, header?.po_vendor_id, header?.vendorId)
  );
  const hasHeaderVendorName =
    header && Object.prototype.hasOwnProperty.call(header, "vendor_name");
  const vendorName = hasHeaderVendorName
    ? String(header.vendor_name ?? "").trim()
    : String(firstItem?.vendor_name ?? "").trim();

  // Header cleared both fields — do not resurrect vendor from line items.
  const headerDeclaresNoVendor =
    !headerVendorId && hasHeaderVendorName && !vendorName;

  let vendorId = headerVendorId;
  if (!vendorId && !headerDeclaresNoVendor) {
    vendorId = normalizeVendorId(
      firstNonEmptyId(firstItem?.vendor_id, firstItem?.po_vendor_id)
    );
  }

  return { vendorId, vendorName, hasVendor: Boolean(vendorId || vendorName) };
};

/** Only `"1"` hides Cost Price. A missing key or any other value leaves it shown. */
const isDisableCostPriceInPoValue = (value) =>
  String(value ?? "").trim() === "1";

/** First object that actually has the key wins (response root, then po_data / header). */
export const resolveDisableCostPriceInPo = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    if (!Object.prototype.hasOwnProperty.call(source, "disable_cost_price_in_po")) {
      continue;
    }
    return isDisableCostPriceInPoValue(source.disable_cost_price_in_po);
  }
  return false;
};

/** Map new_purchase_order_details payload → detail page view model. */
export const mapApiPurchaseOrderDetail = (raw) => {
  const header = resolvePoDetailHeader(raw);
  const rawItems = coerceOrderItemsArray(resolvePoDetailItems(raw, header));
  const lineItems = rawItems.map(mapApiOrderItem);
  const firstItem = rawItems[0] || {};

  const backendId = resolvePurchaseOrderBackendId(header);
  const { vendorId, vendorName, hasVendor } = resolveApiPoVendorFields(
    header,
    firstItem
  );

  return {
    id: backendId || "",
    backendId: backendId || "",
    merchantId: header?.merchant_id != null ? String(header.merchant_id) : "",
    orderingFor:
      header?.order_for != null && String(header.order_for).trim() !== ""
        ? String(header.order_for)
        : header?.merchant_id != null
          ? String(header.merchant_id)
          : "",
    vendorId,
    vendorName,
    poNumber:
      header?.po_number != null && header?.po_number !== ""
        ? String(header.po_number)
        : "",
    status: mapApiStatus(header),
    createdAt:
      normalizeApiDate(header?.po_issued_date) ||
      normalizeApiDate(header?.issued_date) ||
      normalizeApiDate(header?.created_at),
    expectedAt:
      normalizeApiDate(header?.po_stock_date) ||
      normalizeApiDate(header?.stock_date),
    reference:
      header?.inv_number ??
      header?.po_inv_number ??
      header?.vendor_reference ??
      "",
    note: header?.po_reference ?? header?.reference ?? "",
    totalCost:
      header?.total_cost != null && header?.total_cost !== ""
        ? Number(header.total_cost) || 0
        : undefined,
    totalItems: Number(header?.total_items) || lineItems.length,
    totalUnits:
      Number(header?.total_required_qty ?? header?.total_pending_qty) ||
      lineItems.reduce((sum, li) => sum + (Number(li.qtyOrdered) || 0), 0),
    totalReceived:
      Number(header?.total_received_qty) ||
      lineItems.reduce((sum, li) => sum + (Number(li.qtyReceived) || 0), 0),
    vendor: hasVendor
      ? {
          id: vendorId,
          name: vendorName,
          email: header?.vendor_email || firstItem?.vendor_email || "",
          phone: header?.vendor_phone || firstItem?.vendor_phone || "",
          contact:
            header?.vendor_contact_name ||
            header?.vendor_contact ||
            firstItem?.vendor_contact_name ||
            "",
          terms:
            header?.vendor_payment_terms ||
            header?.vendor_terms ||
            header?.payment_terms ||
            firstItem?.vendor_payment_terms ||
            "",
        }
      : null,
    lineItems,
    receiveHistory: mapApiReceiveHistory(raw),
    disableCostPriceInPo: resolveDisableCostPriceInPo(raw, header),
    _raw: { header, items: rawItems },
  };
};

/** Build multipart body for new_purchase_orders_api/new_purchase_order_details. */
export const buildPurchaseOrderDetailsPayload = ({
  auth,
  merchantId,
  poId,
  storeIds,
}) => ({
  ...auth,
  merchant_id: merchantId,
  po_id: String(poId ?? "").trim(),
  // Related-store ids so a PO raised for another location resolves on cold open.
  store_ids: Array.isArray(storeIds)
    ? storeIds.filter(Boolean).join(",")
    : String(storeIds || ""),
});

/** API returns status:false when a PO exists but has no line items yet. */
export const isPurchaseOrderNoItemsMessage = (message) =>
  /no items found/i.test(String(message ?? "").trim());

const resolvePurchaseOrderDetailsRoot = (responseData) =>
  responseData.po_data != null || responseData.po_items_data != null
    ? responseData
    : responseData.data && typeof responseData.data === "object"
      ? responseData.data
      : responseData.result && typeof responseData.result === "object"
        ? responseData.result
        : responseData;

const buildEmptyPurchaseOrderDetail = (detailRoot) => {
  const hasDetailPayload =
    detailRoot &&
    typeof detailRoot === "object" &&
    (detailRoot.po_data != null ||
      detailRoot.purchase_order != null ||
      detailRoot.po_number != null ||
      detailRoot.po_id != null);

  if (!hasDetailPayload) {
    return {
      lineItems: [],
      totalItems: 0,
      totalUnits: 0,
      totalReceived: 0,
      totalCost: 0,
      noProducts: true,
    };
  }

  const mapped = mapApiPurchaseOrderDetail({
    ...detailRoot,
    po_items_data: [],
    order_items: [],
  });
  return {
    ...mapped,
    lineItems: [],
    totalItems: 0,
    totalUnits: 0,
    totalReceived: 0,
    totalCost: 0,
    noProducts: true,
  };
};

const withDisableCostPriceInPo = (detail, responseData, detailRoot) => {
  if (!detail || typeof detail !== "object") return detail;
  return {
    ...detail,
    disableCostPriceInPo: resolveDisableCostPriceInPo(
      responseData,
      detailRoot,
      resolvePoDetailHeader(detailRoot)
    ),
  };
};

export const parsePurchaseOrderDetailsResponse = (responseData) => {
  if (!responseData || typeof responseData !== "object") {
    return { ok: false, message: "Invalid response", detail: null };
  }

  const status =
    responseData.status === true ||
    responseData.status === 1 ||
    responseData.status === "1" ||
    responseData.status === "true";

  const message = String(responseData.message ?? "").trim();
  const detailRoot = resolvePurchaseOrderDetailsRoot(responseData);

  if (!status) {
    if (isPurchaseOrderNoItemsMessage(message)) {
      return {
        ok: true,
        noProducts: true,
        message,
        detail: withDisableCostPriceInPo(
          buildEmptyPurchaseOrderDetail(detailRoot),
          responseData,
          detailRoot
        ),
      };
    }
    return {
      ok: false,
      message: message || "Failed to load purchase order",
      detail: null,
    };
  }

  const detail = withDisableCostPriceInPo(
    mapApiPurchaseOrderDetail(detailRoot),
    responseData,
    detailRoot
  );
  if (!detail.lineItems.length && isPurchaseOrderNoItemsMessage(message)) {
    return {
      ok: true,
      noProducts: true,
      message,
      detail: { ...detail, noProducts: true },
    };
  }

  return {
    ok: true,
    message,
    detail,
  };
};

/** Build multipart body for new_purchase_orders_api/delete_new_purchase_order. */
export const buildDeletePurchaseOrderPayload = ({
  auth,
  merchantId,
  poId,
  employeeId,
}) =>
  withAuditLogAuth(
    {
      ...auth,
      merchant_id: merchantId,
      po_id: String(poId ?? "").trim(),
      employee_id: employeeId ? String(employeeId) : "0",
    },
    auth,
    employeeId
  );

export const parseDeletePurchaseOrderResponse = (responseData) => {
  if (!responseData || typeof responseData !== "object") {
    return { ok: false, message: "Invalid response" };
  }

  const status =
    responseData.status === true ||
    responseData.status === 1 ||
    responseData.status === "1" ||
    responseData.status === "true";

  const data =
    responseData.data && typeof responseData.data === "object"
      ? responseData.data
      : null;

  const message = String(responseData.message ?? data?.message ?? "").trim();

  return {
    ok: status,
    message,
  };
};

export const buildVoidPurchaseOrderPayload = buildDeletePurchaseOrderPayload;

export const parseVoidPurchaseOrderResponse = parseDeletePurchaseOrderResponse;

const resolvePoReceivedStatusApiValue = (po) => {
  const header = po?._raw?.header || {};
  const fromHeader = header?.received_status ?? header?.po_received_status;
  if (fromHeader != null && String(fromHeader).trim() !== "") {
    return String(fromHeader);
  }
  return statusToReceivedStatus(po?.status);
};

/** Header fields the update API needs when flipping draft → active (mirrors add_new_purchase_order). */
const buildPurchaseOrderUpdateHeaderFields = (
  po,
  merchantId,
  { expectedAt, reference, note } = {}
) => {
  const header = po?._raw?.header || {};
  const issuedDate =
    normalizeApiDate(po?.createdAt) ||
    normalizeApiDate(header?.issued_date || header?.po_issued_date) ||
    dayjs().format("YYYY-MM-DD");
  const orderingFor =
    po?.orderingFor ||
    po?.merchantId ||
    header?.order_for ||
    merchantId ||
    "";
  const deliverTo =
    header?.deliver_to != null && String(header.deliver_to).trim() !== ""
      ? String(header.deliver_to)
      : String(orderingFor);

  return {
    issued_date: issuedDate,
    order_for: String(orderingFor),
    deliver_to: deliverTo,
    is_void: "0",
    stock_date: formatStockDueForApi(expectedAt ?? po?.expectedAt),
    inv_number: String(reference ?? po?.reference ?? ""),
    reference: String(note ?? po?.note ?? ""),
  };
};

/** Build multipart body for new_purchase_orders_api/update_new_purchase_order_items. */
export const buildUpdatePurchaseOrderPayload = ({
  auth,
  merchantId,
  employeeId,
  employeeName,
  po,
  apiPoId,
  expectedAt,
  reference,
  note,
  deletedItemIds = [],
  isDraft,
  receivedStatus,
}) => {
  const orderItems = buildUpdateOrderItemsPayload(po?.lineItems);
  const vendorName = String(po?.vendor?.name || po?.vendorName || "").trim();
  // Prefer explicit id; if the UI still shows a vendor name, also accept vendor.id
  // so unchanged vendors are not sent as name-only (API would clear vendor_id).
  const vendorId =
    resolvePurchaseOrderVendorId(po) ||
    (vendorName ? normalizeVendorId(po?.vendor?.id) : "");
  const delItemIds = (deletedItemIds || [])
    .map((id) => String(id || "").trim())
    .filter(Boolean)
    .join(",");
  const poId = String(
    apiPoId || resolvePurchaseOrderApiPoId({ po }) || ""
  ).trim();

  const payload = {
    ...auth,
    merchant_id: merchantId,
    admin_id: merchantId,
    employee_id: employeeId ? String(employeeId) : "",
    employee_name: String(employeeName || "").trim(),
    po_id: poId,
    po_number: sanitizeOrderNumber(po?.poNumber).trim(),
    vendor_id: resolveVendorIdForApi(vendorId),
    vendor_name: resolveVendorNameForApi(vendorId, vendorName),
    received_status:
      receivedStatus != null
        ? String(receivedStatus)
        : computePoReceivedStatusFromLines(po?.lineItems),
    ...buildPurchaseOrderUpdateHeaderFields(po, merchantId, {
      expectedAt,
      reference,
      note,
    }),
    order_items: JSON.stringify(orderItems),
    ...(delItemIds ? { del_item_ids: delItemIds } : {}),
  };

  if (isDraft !== undefined) {
    payload.is_draft = isDraft ? "1" : "0";
  }

  return payload;
};

/** Draft → active via update_new_purchase_order_items (`is_draft: 0`, `received_status: 0`). */
export const buildActivatePurchaseOrderPayload = (params) =>
  buildUpdatePurchaseOrderPayload({
    ...params,
    isDraft: false,
    receivedStatus: "0",
  });

export const parseUpdatePurchaseOrderResponse = (responseData) => {
  if (!responseData || typeof responseData !== "object") {
    return { ok: false, message: "Invalid response" };
  }

  const status =
    responseData.status === true ||
    responseData.status === 1 ||
    responseData.status === "1" ||
    responseData.status === "true";

  const data =
    responseData.data && typeof responseData.data === "object"
      ? responseData.data
      : null;

  let message = String(responseData.message ?? data?.message ?? "").trim();
  if (!message && responseData.errors && typeof responseData.errors === "object") {
    const first = Object.values(responseData.errors)[0];
    message = String(
      typeof first === "string" ? first : first?.msg || ""
    ).trim();
  }

  return {
    ok: status,
    message,
  };
};

/**
 * Multipart body for new_purchase_orders_api/update_new_purchase_order_items_soh.
 * Stock on Hand only — does not change the PO header or delete existing lines.
 */
export const buildUpdatePurchaseOrderItemsSohPayload = ({
  auth,
  merchantId,
  employeeId,
  apiPoId,
  poNumber,
  vendorId,
  vendorName,
  lineItems,
  receivedStatus = "0",
}) => {
  const orderItems = buildSohUpdateOrderItemsPayload(lineItems);
  const poId = String(apiPoId || "").trim();

  return withAuditLogAuth(
    {
      ...auth,
      merchant_id: merchantId,
      admin_id: merchantId,
      po_id: poId,
      po_number: sanitizeOrderNumber(poNumber).trim(),
      vendor_id: resolveVendorIdForApi(vendorId) ?? "",
      vendor_name: resolveVendorNameForApi(vendorId, vendorName) ?? "",
      received_status: String(receivedStatus ?? "0"),
      order_items: JSON.stringify(orderItems),
    },
    auth,
    employeeId
  );
};

/** Map receive modal rows → `order_items` for receive_new_purchase_order. */
export const buildReceiveOrderItemsPayload = ({ po, receiveItems }) => {
  const poId = String(po?.backendId || po?.id || "").trim();
  const poNumber = sanitizeOrderNumber(po?.poNumber).trim();

  return (receiveItems || []).reduce((acc, entry, index) => {
    const li = entry?.line;
    const receiveQty = Math.max(0, Number(entry?.receiveQty) || 0);
    if (!li || receiveQty <= 0) return acc;

    const required = Number(li.qtyOrdered) || 0;
    const prevReceived = Number(li.qtyReceived) || 0;
    const afterReceived = prevReceived + receiveQty;
    const pending = Math.max(0, required - afterReceived);
    const cost = Number(li.unitCost) || 0;
    // Snapshot selling price from product API — do not recompute or edit.
    const price = Number(li.price) || 0;
    const inventory = Number(li.currentInventory) || 0;
    const variantId =
      li.variantId != null && String(li.variantId).trim() !== ""
        ? String(li.variantId)
        : "";

    acc[index] = {
      po_id: poId,
      po_number: poNumber,
      product_id: String(li.productId || ""),
      variant_id: variantId,
      required_qty: String(required),
      // API expects qty received in *this* receive action only (not cumulative).
      recieved_qty: String(receiveQty),
      pending_qty: String(pending),
      recieved_status: lineItemReceivedStatus(required, afterReceived),
      after_qty: String(inventory + receiveQty),
      cost_per_item: formatMoneyForApi(cost),
      price: formatMoneyForApi(price),
      total_pricing: formatMoneyForApi(receiveQty * cost),
      upc: String(li.sku || li.upc || ""),
      note: String(li.note || ""),
      title: String(li.title || li.label || ""),
      external_id: "",
      po_item_id: String(li.poItemId ?? li.po_item_id ?? ""),
    };
    return acc;
  }, {});
};

const pickEmployeeNameFromRecord = (record = {}) => {
  if (!record || typeof record !== "object") return "";
  const fName = String(
    record?.f_name ?? record?.fname ?? record?.first_name ?? ""
  ).trim();
  const lName = String(
    record?.l_name ?? record?.lname ?? record?.last_name ?? ""
  ).trim();
  const fullName = `${fName} ${lName}`.trim();
  if (fullName) return fullName;
  return String(record?.employee_name ?? record?.emp_name ?? "").trim();
};

/** Display name for the logged-in user (employee f_name + l_name when available). */
export const resolveSessionEmployeeName = (loginJson = {}) => {
  const employeeId =
    loginJson?.employee_id ?? loginJson?.data?.employee_id ?? "";
  const hasEmployeeLogin =
    employeeId != null &&
    String(employeeId).trim() !== "" &&
    String(employeeId) !== "0";

  const sources = [
    loginJson,
    loginJson?.data,
    loginJson?.data2,
    loginJson?.employee,
    loginJson?.data?.employee,
  ].filter((src) => src && typeof src === "object");

  for (const src of sources) {
    const name = pickEmployeeNameFromRecord(src);
    if (name) return name;
  }

  if (!hasEmployeeLogin) {
    const merchantName = String(
      loginJson?.data?.owner_name ??
        loginJson?.data?.name ??
        loginJson?.name ??
        ""
    ).trim();
    if (merchantName) return merchantName;
  }

  return "";
};

/** Resolve display name for receive PO — session first, then employee lookup (PO module only). */
export const resolvePoEmployeeName = async ({
  loginJson,
  auth,
  merchantId,
  employeeId,
}) => {
  const fromSession = resolveSessionEmployeeName(loginJson);
  if (fromSession) return fromSession;

  const empId = String(employeeId ?? "").trim();
  const storeId = String(merchantId ?? "").trim();
  if (!empId || empId === "0" || !storeId || !auth?.token) return "";

  try {
    const { token, ...body } = {
      merchant_id: storeId,
      employee_id: empId,
      login_type: auth.login_type,
      token: auth.token,
      token_id: auth.token_id,
      session_id: auth.session_id,
    };
    const response = await axios.post(ciUrl(EMPLOYEE_DATA), body, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });
    const employeeData =
      response?.data?.data ||
      response?.data?.result ||
      response?.data?.employeeData ||
      {};
    return pickEmployeeNameFromRecord(employeeData);
  } catch {
    return "";
  }
};

/** Build multipart body for new_purchase_orders_api/receive_new_purchase_order. */
export const buildReceivePurchaseOrderPayload = ({
  auth,
  merchantId,
  employeeId,
  employeeName,
  po,
  receiveItems,
  receiveNote,
}) => {
  const eligibleItems = (receiveItems || []).filter(
    (item) => Math.max(0, Number(item?.receiveQty) || 0) > 0
  );
  const totalUnits = eligibleItems.reduce(
    (sum, item) => sum + (Number(item.receiveQty) || 0),
    0
  );
  const affectedLines = eligibleItems.length;

  const receiveByKey = Object.fromEntries(
    eligibleItems.map((item) => [
      String(item.variantKey ?? ""),
      Number(item.receiveQty) || 0,
    ])
  );
  const projectedLines = (po?.lineItems || []).map((li) => ({
    ...li,
    qtyReceived:
      (Number(li.qtyReceived) || 0) +
      (Number(receiveByKey[String(li.variantKey ?? "")]) || 0),
  }));
  const orderItems = buildReceiveOrderItemsPayload({
    po,
    receiveItems: eligibleItems,
  });
  const vendorId = resolvePurchaseOrderVendorId(po);
  const vendorName = String(po?.vendor?.name || po?.vendorName || "").trim();

  return withAuditLogAuth(
    {
      ...auth,
      merchant_id: merchantId,
      po_id: String(po?.backendId || po?.id || "").trim(),
      vendor_id: resolveVendorIdForApi(vendorId),
      vendor_name: resolveVendorNameForApi(vendorId, vendorName),
      employee_id: employeeId ? String(employeeId) : "0",
      employee_name: String(employeeName || "").trim(),
      received_status: computePoReceivedStatusFromLines(projectedLines),
      // API `reference` = internal note column; `po.reference` is the vendor ref (inv_number).
      reference: String(po?.note ?? ""),
      received_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      total_units: String(totalUnits),
      affected_lines: String(affectedLines),
      note: String(po?.note ?? ""),
      po_note: String(receiveNote ?? "").trim(),
      order_items: JSON.stringify(orderItems),
    },
    auth,
    employeeId
  );
};

export const parseReceivePurchaseOrderResponse = parseUpdatePurchaseOrderResponse;

const parseCsvImportCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/** Parse CSV import body — skipped/imported counts, UPCs, message (success or failure). */
export const parseCsvImportMeta = (responseData) => {
  if (!responseData || typeof responseData !== "object") {
    return {
      message: "",
      skippedUpcs: [],
      skippedProducts: 0,
      importedProducts: 0,
      poNumber: "",
    };
  }

  const data =
    responseData.data && typeof responseData.data === "object"
      ? responseData.data
      : null;

  const rawSkipped =
    responseData.skipped_upcs ??
    responseData.skippedUpcs ??
    data?.skipped_upcs ??
    data?.skippedUpcs;

  let skippedUpcs = [];
  if (Array.isArray(rawSkipped)) {
    skippedUpcs = rawSkipped.map((upc) => String(upc || "").trim()).filter(Boolean);
  } else if (rawSkipped && typeof rawSkipped === "object") {
    skippedUpcs = Object.values(rawSkipped)
      .map((upc) => String(upc || "").trim())
      .filter(Boolean);
  }

  const skippedProducts = parseCsvImportCount(
    responseData.skipped_products ??
      responseData.skippedProducts ??
      data?.skipped_products ??
      data?.skippedProducts
  );

  const importedProducts = parseCsvImportCount(
    responseData.imported_products ??
      responseData.importedProducts ??
      data?.imported_products ??
      data?.importedProducts
  );

  const poNumber = String(
    responseData.po_number ?? responseData.poNumber ?? data?.po_number ?? data?.poNumber ?? ""
  ).trim();

  const message = String(responseData.message ?? data?.message ?? "").trim();

  return {
    message,
    skippedUpcs,
    skippedProducts: skippedProducts || skippedUpcs.length,
    importedProducts,
    poNumber,
  };
};

/** Parse CSV import failure body (400 or status:false) — skipped UPCs + message. */
export const parseCsvImportErrorBody = (responseData) => {
  const meta = parseCsvImportMeta(responseData);
  return {
    message: meta.message,
    skippedUpcs: meta.skippedUpcs,
    skippedProducts: meta.skippedProducts,
  };
};

/** User-facing message when CSV rows were skipped (invalid/unknown UPCs). */
export const formatCsvImportSkippedMessage = ({
  skippedProducts = 0,
  skippedUpcs = [],
} = {}) => {
  const count = skippedProducts || skippedUpcs.length;
  if (!count) return "";

  const upcList = skippedUpcs.join(", ");
  if (upcList) {
    const upcLabel = skippedUpcs.length === 1 ? "UPC" : "UPCs";
    return `${count} product${count === 1 ? "" : "s"} could not be imported (${upcLabel}: ${upcList}).`;
  }

  return `${count} product${count === 1 ? "" : "s"} could not be imported.`;
};

export const parseCreatePurchaseOrderResponse = (responseData) => {
  if (!responseData || typeof responseData !== "object") {
    return { ok: false, message: "Invalid response", poId: "" };
  }

  const status =
    responseData.status === true ||
    responseData.status === 1 ||
    responseData.status === "1" ||
    responseData.status === "true";

  const data =
    responseData.data && typeof responseData.data === "object"
      ? responseData.data
      : null;

  const poId = String(
    data?.po_id ??
    data?.id ??
    data?.purchase_order_id ??
    responseData.po_id ??
    ""
  ).trim();

  const message = String(responseData.message ?? data?.message ?? "").trim();
  const csvMeta = parseCsvImportMeta(responseData);
  const poNumber = csvMeta.poNumber;

  return {
    ok: status,
    message,
    poId,
    poNumber,
    importedProducts: csvMeta.importedProducts,
    skippedProducts: csvMeta.skippedProducts,
    skippedUpcs: csvMeta.skippedUpcs,
  };
};

/** Send-vendor email — preserve draft/active state; email only, no status flip. */
const buildSendPurchaseOrderPreserveStatusFields = (po) => {
  if (!po) return {};

  const header = po?._raw?.header || {};
  const issuedDate =
    normalizeApiDate(po.createdAt) ||
    normalizeApiDate(header?.issued_date || header?.po_issued_date) ||
    dayjs().format("YYYY-MM-DD");

  return {
    issued_date: issuedDate,
    stock_date: formatStockDueForApi(po.expectedAt),
    reference: String(po.note ?? po.reference ?? ""),
    is_draft: po.status === "draft" ? "1" : "0",
    received_status: resolvePoReceivedStatusApiValue(po),
  };
};

/** Build fields for new_purchase_orders_api/send_vendor_email (attachment appended separately). */
export const buildSendPurchaseOrderEmailPayload = ({
  auth,
  merchantId,
  poId,
  to,
  subject,
  message,
  po,
}) => ({
  ...auth,
  merchant_id: merchantId,
  po_id: String(poId ?? "").trim(),
  to: String(to ?? "").trim(),
  subject: String(subject ?? "").trim(),
  message: String(message ?? "").trim(),
  ...buildSendPurchaseOrderPreserveStatusFields(po),
});

export const parseSendPurchaseOrderEmailResponse = (responseData) => {
  if (!responseData || typeof responseData !== "object") {
    return { ok: false, message: "Invalid response" };
  }

  const status =
    responseData.status === true ||
    responseData.status === 1 ||
    responseData.status === "1" ||
    responseData.status === "true";

  const message = String(responseData.message ?? responseData.data?.message ?? "").trim();

  return {
    ok: status,
    message: message || (status ? "Email sent" : "Failed to send email"),
  };
};
